/**
 * Descriptive alt-text generation for auction imagery.
 *
 * Builds accessible, human-readable alt text from the card's own metadata
 * (grading company, grade, name, set) rather than a generic filename:
 *
 *   "PSA 10 Charizard Base Set Trading Card"
 */

export type AltSource = {
  title?: string | null;
  grading_company?: string | null;
  custom_grading_company?: string | null;
  grade?: string | null;
  custom_grade?: string | null;
  card_set?: string | null;
  card_number?: string | null;
  product_type?: string | null;
  accessory_category?: string | null;
  brand?: string | null;
};

function clean(v: string | null | undefined): string | null {
  const s = (v ?? "").trim();
  if (!s) return null;
  if (/^(raw|none|n\/a|other)$/i.test(s)) return null;
  return s;
}

/** Human label for the product family, used as the alt-text suffix. */
function kindLabel(src: AltSource): string {
  const t = (src.product_type ?? "trading_card").toLowerCase();
  if (t === "accessory") return clean(src.accessory_category) ?? "Trading Card Accessory";
  if (t === "sealed_product") return "Sealed Trading Card Product";
  return "Trading Card";
}

/**
 * Descriptive alt text for the primary image of an auction.
 * Falls back to the listing title alone when metadata is sparse.
 */
export function buildAuctionAlt(src: AltSource): string {
  const title = clean(src.title) ?? "Trading card";
  const grader = clean(src.grading_company) ?? clean(src.custom_grading_company);
  const grade = clean(src.grade) ?? clean(src.custom_grade);
  const set = clean(src.card_set);
  const kind = kindLabel(src);

  const parts: string[] = [];
  if (grader) parts.push(grader);
  if (grade) parts.push(grade);
  parts.push(title);
  if (set && !title.toLowerCase().includes(set.toLowerCase())) parts.push(set);
  parts.push(kind);

  return parts.join(" ").replace(/\s+/g, " ").trim();
}

/** Alt text for a secondary/gallery image: base description + position. */
export function buildAuctionImageAlt(src: AltSource, index: number, total: number): string {
  const base = buildAuctionAlt(src);
  return total > 1 ? `${base} — photo ${index + 1} of ${total}` : base;
}
